import { debounce } from 'es-toolkit';
import React from 'react';

import { useRouterStore } from '@/store/routerStore';

interface UseScrollRestorationProps {
  readyToScroll?: boolean;
  getScrollableContainer: () => Element | null;
  subTabValue?: string;
}

export const useScrollRestoration = ({
  readyToScroll = true,
  getScrollableContainer,
  subTabValue,
}: UseScrollRestorationProps) => {
  const selectedTab = useRouterStore((state) => state.selectedTabId);
  const scrollPositions = useRouterStore((state) => state.scrollPositions);
  const saveScrollPosition = useRouterStore((state) => state.saveScrollPosition);

  const scrollPositionsRef = React.useRef(scrollPositions);

  const savePosition = React.useCallback(
    (scrollY: number) => {
      saveScrollPosition({
        mainTabValue: selectedTab,
        subTabValue,
        scrollTop: scrollY,
      });
    },
    [saveScrollPosition, selectedTab, subTabValue],
  );

  const getLastScrollPosition = React.useCallback(() => {
    return scrollPositionsRef.current.find((pos) => {
      const mainTabValueMatches = pos.mainTabValue === selectedTab;

      return mainTabValueMatches && pos.subTabValue === subTabValue;
    });
  }, [selectedTab, subTabValue]);

  React.useEffect(() => {
    scrollPositionsRef.current = scrollPositions;
  }, [scrollPositions]);

  React.useEffect(() => {
    const container = getScrollableContainer();

    const lastScrollPosition = getLastScrollPosition();

    if (container && readyToScroll && lastScrollPosition) {
      container.scrollTo(0, lastScrollPosition.scrollTop);
    }

    if (container) {
      const observerCallback: IntersectionObserverCallback = (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          if (readyToScroll) {
            const currentLastScrollPosition = getLastScrollPosition();
            if (currentLastScrollPosition) {
              container.scrollTo(0, currentLastScrollPosition.scrollTop);
            }
            if (observer) {
              observer.unobserve(container);
            }
          }
        }
      };

      const observer = new IntersectionObserver(observerCallback, {});

      observer.observe(container);

      return () => {
        observer.unobserve(container);
      };
    }

    return () => {};
  }, [getLastScrollPosition, getScrollableContainer, readyToScroll]);

  React.useEffect(() => {
    const container = getScrollableContainer();

    let latestScrollTop: number | undefined;

    if (container) {
      const debouncedSaveScrollPosition = debounce(() => {
        const scrollY = container.scrollTop;
        savePosition(scrollY);
        latestScrollTop = scrollY;
      }, 300);

      container.addEventListener('scroll', debouncedSaveScrollPosition);

      return () => {
        container.removeEventListener('scroll', debouncedSaveScrollPosition);
        debouncedSaveScrollPosition.cancel();

        if (typeof latestScrollTop === 'number') {
          savePosition(latestScrollTop);
        }
      };
    }

    return () => {};
  }, [getScrollableContainer, savePosition]);
};
